import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faSync,
  faInfoCircle,
  faCheckCircle,
  faExclamationTriangle,
  faTruck,
  faBox,
  faTruckFast
} from '@fortawesome/free-solid-svg-icons';
import { configService } from '../../../services/configService';
import { useAuth } from '../../../AuthContext';

const StatusDescriptions = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [origin, setOrigin] = useState('');
  const [inTransit, setInTransit] = useState('');
  const [outForDelivery, setOutForDelivery] = useState('');
  const [delivered, setDelivered] = useState('');

  const refreshFromServer = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await configService.getStatusConfig();
      const cfg = resp?.config || {};
      
      setOrigin(cfg.origin || '');
      setInTransit(cfg.inTransit || '');
      setOutForDelivery(cfg.outForDelivery || '');
      setDelivered(cfg.delivered || '');
    } catch (e) {
      setError('Failed to load status descriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshFromServer();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await configService.updateStatusConfig({
        origin,
        inTransit,
        outForDelivery,
        delivered
      });
      
      setSuccess('Status descriptions updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600">You do not have permission to view this page.</p>
          <button
            onClick={() => navigate('/dashboard/user')}
            className="mt-6 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard/admin')}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-600 transition"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Manage Status Descriptions</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={refreshFromServer}
                disabled={loading || saving}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                title="Refresh"
              >
                <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={handleSave}
                disabled={loading || saving}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition shadow-sm"
              >
                {saving ? (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Messages */}
        {(error || success) && (
          <div className={`mb-6 p-4 rounded-xl flex items-start ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            <FontAwesomeIcon icon={error ? faExclamationTriangle : faCheckCircle} className="mt-0.5 mr-3" />
            <p>{error || success}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mr-2" />
              Tracking Status Messages
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Customize the messages shown to users at different stages of their shipment journey.
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Origin / Pending */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mr-2 text-xs">
                  <FontAwesomeIcon icon={faBox} />
                </span>
                Origin / Pickup Pending
              </label>
              <textarea
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent transition text-sm text-gray-700"
                placeholder="Message shown when shipment is at origin..."
              />
              <p className="text-xs text-gray-400 mt-1">Default: Your shipment has been registered and payment confirmed. Waiting for courier pickup from sender.</p>
            </div>

            {/* In Transit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-lg bg-yellow-100 text-yellow-600 flex items-center justify-center mr-2 text-xs">
                  <FontAwesomeIcon icon={faTruck} />
                </span>
                In Transit
              </label>
              <textarea
                value={inTransit}
                onChange={(e) => setInTransit(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent transition text-sm text-gray-700"
                placeholder="Message shown when shipment is moving..."
              />
              <p className="text-xs text-gray-400 mt-1">Default: Your package is on its way to the destination city. It's being transported through our delivery network.</p>
            </div>

            {/* Out for Delivery */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center mr-2 text-xs">
                  <FontAwesomeIcon icon={faTruckFast} />
                </span>
                Out for Delivery
              </label>
              <textarea
                value={outForDelivery}
                onChange={(e) => setOutForDelivery(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent transition text-sm text-gray-700"
                placeholder="Message shown when courier has the package..."
              />
              <p className="text-xs text-gray-400 mt-1">Default: Your package is with the delivery courier and will be delivered today.</p>
            </div>

            {/* Delivered */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center mr-2 text-xs">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </span>
                Delivered
              </label>
              <textarea
                value={delivered}
                onChange={(e) => setDelivered(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent transition text-sm text-gray-700"
                placeholder="Message shown when delivered..."
              />
              <p className="text-xs text-gray-400 mt-1">Default: Your package has been successfully delivered to the recipient.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StatusDescriptions;
